import React, { useMemo, useState, useCallback, useRef } from 'react'
import { BaseDialog } from '@/components/dialog/base-dialog'
import { layerManage } from '@/utils/layer'
import { ColumnsType } from 'antd/lib/table'
import { refundGoodsList } from './order-refund'
import { Table, Row, Radio, Tooltip, Spin } from 'antd'
import useBemClass from '@/utils/hooks/useBEMClass'
import './order-refund.scss'
import Button from '@/components/button/button'
import Iconfont from '@/components/iconfont/iconfont'
import { ICashierOrder } from '../order-list/order-table/type'
import { useRequest } from 'ahooks'
import { $post } from '@/api'
import $msg from '@/utils/message'
import { cashierOrderStore } from '../store'

interface Payment {
    payment_type: number
    payment_amount: number
}

interface OrderRefundConfirmProps {
    refundGoods: refundGoodsList[]
    payments: ICashierOrder.Payment[]
    remark: string
    subtotal: number
    orderId: number
    orderType: number
}

export const useRefund = (payments: Payment[]) => {
    const payType = useRef([
        {
            name: '个人微信',
            des: '商家通过个人微信自主转账给消费者',
            id: 1,
        },
        {
            name: '个人支付宝',
            des: '商家通过个人支付宝自主转账给消费者',
            id: 2,
        },
        {
            name: '现金',
            id: 3,
        },
        {
            name: '银行卡',
            des: '商家通过银行卡自主转账给消费者',
            id: 4,
        },
    ])

    const isIncludePayment = useCallback(
        (payment_type: number[]) => {
            const r = payments.filter(r => payment_type.includes(r.payment_type))

            return !!r.length
        },
        [payments],
    )

    const [selectType, setSelectType] = useState<number>(isIncludePayment([4]) ? 5 : -1)

    const customPaymentData = useRequest<{
        custom_payment_config: Array<{
            id: number
            name: string
        }>
    }>(
        () =>
            $post({
                url: '/shop/getshoppayment',
            }),
        {
            loadingDelay: 200, // 加载延时，防止闪烁
        },
    )

    const isOnlinePay = useMemo(() => {
        return isIncludePayment([2, 3, 5, 6, 7, 8, 13, 14, 15, 16, 17, 19, 20, 21, 22])
    }, [isIncludePayment])

    const paymentType = useMemo(() => {
        const allPayType = customPaymentData.data
            ? [
                  ...payType.current,
                  ...customPaymentData.data.custom_payment_config.map(p => ({
                      id: parseInt(`6${p.id}`),
                      name: p.name,
                      des: '',
                  })),
              ]
            : payType.current

        if (!isIncludePayment([1]) && isOnlinePay && !(isOnlinePay && isIncludePayment([4]))) {
            return [
                {
                    name: '在线支付',
                    des: '原支付方式退回',
                    id: 0,
                },
                ...allPayType,
            ]
        }
        if (isIncludePayment([4])) {
            return [
                {
                    name: '个人余额',
                    id: 5,
                },
                ...allPayType,
            ]
        }

        return allPayType
    }, [customPaymentData.data, isIncludePayment, isOnlinePay])

    return {
        paymentType,
        selectType,
        setSelectType,
        customPaymentData,
        isOnlinePay,
    }
}

const OrderRefundConfirm: React.FC<OrderRefundConfirmProps> = props => {
    const cls = useBemClass('cashier-order-refund')

    const { selectType, setSelectType, paymentType, customPaymentData } = useRefund(props.payments)

    const refundReq = useRequest(
        () =>
            $post({
                url: '/order/refundorder',
                params: {
                    order_id: props.orderId,
                    order_type: 1,
                    refund_subtotal: props.subtotal,
                    refund_goods: props.refundGoods
                        .filter(g => g.return_stock !== 2)
                        .map(g => ({
                            order_goods_id: g.id,
                            refund_amount: g.refund_amount,
                            refund_subtotal: g.refund_subtotal,
                            return_stock: !!g.return_stock,
                        })),
                    refund_type: selectType.toString().startsWith('6') ? 6 : selectType,
                    refund_mark: props.remark,
                    custom_payment_id: selectType.toString().startsWith('6')
                        ? +selectType.toString().substring(1)
                        : undefined,
                },
            }),
        {
            manual: true,
            onSuccess: () => {
                $msg('success', '退款成功')
                layerManage.pop()
                cashierOrderStore.setRefresh()
            },
            onError: e => $msg('error', e.message),
        },
    )

    const columns: ColumnsType<refundGoodsList> = [
        {
            title: '#',
            key: 'index',
            width: 50,
            render: (value, record, index) => index + 1,
        },
        {
            title: '商品名',
            key: 'goods',
            width: 500,
            ellipsis: true,
            dataIndex: 'goods',
            render: val => val.name,
        },
        {
            title: '退货量',
            width: 70,
            render: (v, r) => (r.return_stock ? r.refund_amount : '-'),
        },
        {
            title: '库存',
            render: (v, r) => (r.return_stock ? `+${r.refund_amount}` : '-'),
        },
        {
            title: '退款',
            render: (v, r) => '¥' + r.refund_subtotal,
        },
    ]

    return (
        <BaseDialog isShow title="退款确认" onClose={() => layerManage.pop()}>
            <Spin spinning={customPaymentData.loading}>
                <div className={cls('tip')}>*仅支持退款一次，请仔细确认退款信息</div>
                <div className={cls('confirm-table-container')}>
                    <Table
                        columns={columns}
                        rowKey={r => r.id}
                        dataSource={props.refundGoods}
                        pagination={false}
                        bordered
                        scroll={{ x: 'max-content', y: 225 }}
                    ></Table>
                    <div className={cls('confirm-bottom')}>
                        <Row>
                            <span data-color-333 data-font-size-16>
                                备注：
                            </span>
                            <div className={cls('confirm-remark')}>{props.remark || '无'}</div>
                        </Row>
                        <Row data-align-items-flex-end>
                            <span data-font-size-14>总退款：</span>
                            <span data-font-size-12 data-color-ff4343 data-font-weight-600>
                                ¥
                            </span>
                            <span
                                style={{
                                    lineHeight: '24px',
                                }}
                                data-font-size-24
                                data-color-ff4343
                                data-font-weight-600
                            >
                                {props.subtotal}
                            </span>
                        </Row>
                    </div>
                </div>
                <Row data-center data-aling-items-center>
                    <div className={cls('line')} />
                    <div data-color-999 data-font-size-16 data-margin-h-8 data-margin-v-15>
                        退款方式
                    </div>
                    <div className={cls('line')} />
                </Row>
                <Radio.Group
                    onChange={e => {
                        setSelectType(e.target.value)
                    }}
                    value={selectType}
                >
                    <Row className={cls('group')} data-align-items-center data-padding-left-16>
                        {paymentType.map(p => (
                            <div key={p.id} className={cls('radio-container')} data-align-items-center>
                                <Radio value={p.id}>{p.name}</Radio>
                                {p.des && (
                                    <Tooltip placement="topLeft" trigger={['click', 'hover']} title={p.des}>
                                        <Iconfont
                                            name="icon8yiwen-16"
                                            size={16}
                                            style={{
                                                marginLeft: '-6px',
                                                zIndex: 10,
                                            }}
                                        />
                                    </Tooltip>
                                )}
                            </div>
                        ))}
                    </Row>
                </Radio.Group>
                <Row className={cls('comfirm-btn')}>
                    <Button
                        types="text"
                        style={{
                            marginRight: '16px',
                            color: '#999',
                        }}
                    >
                        取消
                    </Button>
                    <Button
                        disabled={!props.subtotal}
                        types="danger"
                        loading={refundReq.loading}
                        onClick={refundReq.run}
                    >
                        确认退款
                    </Button>
                </Row>
            </Spin>
        </BaseDialog>
    )
}

export default OrderRefundConfirm
